import React, { useEffect, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getFinanzasKpisData,
  getFinanzasEvolucionData,
  getFinanzasGastosData,
  getFinanzasSaldosData,
  getFinanzasFacturasPendientesData,
  getFinanzasFacturasPendientesMesData,
  getFinanzasParcialData,
  getFinanzasPaisesAnualData,
  getFinanzasPaisesParcialData,
  getFinanzasClientesAnualData,
  getFinanzasClientesParcialData,
  getFinanzasAgingCobrosData,
  getFinanzasDsoData,
  getFinanzasTicketMedioAnualData,
  getFinanzasTicketMedioParcialData,
  getFinanzasEficienciaData,
} from "../../slices/dashboardFinanzas/thunk";

import { I } from "../../Components/CMI/icons";
import { KPI, CompactKPI, ChartCard, Badge, BankChip, Legend, Empty } from "../../Components/CMI/primitives";
import { BarLineChart, DonutChart, DonutLegend, HorizontalBarList, Sparkline, fmtEur, fmtN, fmtPct } from "../../Components/CMI/charts";
import PageHead, { SectionHead, Footer } from "../../Layouts/CMI/PageHead";
import { MESES, mesesHastaActual, rangoMes } from "../../helpers/meses";

const MONTHS = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const MESES_OPCIONES = mesesHastaActual();

const deltaPill = (pct) => {
  if (pct == null || isNaN(pct)) return null;
  const pos = pct >= 0;
  return {
    text: `${pos ? "↑" : "↓"} ${Math.abs(pct).toFixed(1).replace(".", ",")}%`,
    tone: pos ? "pos" : "neg",
  };
};

const DashboardFinanzas = () => {
  document.title = "Finanzas | CMI - Grupo Cucalón Estévez";
  const dispatch = useDispatch();
  const { empresaId, year, vista } = useSelector((s) => s.Filters);
  const {
    kpis, evolucion, gastos, saldos, facturasPendientes, facturasPendientesMes, parcial,
    paisesAnual, paisesParcial, clientesAnual, clientesParcial, agingCobros, dso,
    ticketMedioAnual, ticketMedioParcial, eficiencia, loading,
  } = useSelector((s) => s.DashboardFinanzas);
  const [mes, setMes] = useState(new Date().getMonth());

  const loadAll = useCallback(() => {
    dispatch(getFinanzasKpisData());
    dispatch(getFinanzasEvolucionData());
    dispatch(getFinanzasGastosData());
    dispatch(getFinanzasSaldosData());
    dispatch(getFinanzasFacturasPendientesData());
    dispatch(getFinanzasPaisesAnualData());
    dispatch(getFinanzasClientesAnualData());
    dispatch(getFinanzasAgingCobrosData());
    dispatch(getFinanzasDsoData());
    dispatch(getFinanzasTicketMedioAnualData());
    dispatch(getFinanzasEficienciaData());
  }, [dispatch]);

  useEffect(() => { loadAll(); }, [loadAll, empresaId, year]);
  useEffect(() => {
    if (vista !== "mensual") return;
    const filtro = rangoMes(new Date().getFullYear(), mes);
    dispatch(getFinanzasParcialData(filtro));
    dispatch(getFinanzasPaisesParcialData(filtro));
    dispatch(getFinanzasClientesParcialData(filtro));
    dispatch(getFinanzasTicketMedioParcialData(filtro));
    dispatch(getFinanzasFacturasPendientesMesData(filtro));
  }, [dispatch, empresaId, mes, vista]);

  // Listen for manual refresh events from header button
  useEffect(() => {
    const h = () => {
      loadAll();
      const filtro = rangoMes(new Date().getFullYear(), mes);
      dispatch(getFinanzasParcialData(filtro));
      dispatch(getFinanzasPaisesParcialData(filtro));
      dispatch(getFinanzasClientesParcialData(filtro));
      dispatch(getFinanzasTicketMedioParcialData(filtro));
      dispatch(getFinanzasFacturasPendientesMesData(filtro));
    };
    window.addEventListener("cmi:refresh", h);
    return () => window.removeEventListener("cmi:refresh", h);
  }, [loadAll, dispatch, mes]);

  // KPI derivations
  const ingresosDelta  = deltaPill(kpis?.ingresos?.variacion_vs_ano_anterior);
  const ingresosDiff   = kpis?.ingresos?.diferencia_vs_ano_anterior;
  const ingresosDiffTxt = ingresosDiff == null
    ? null
    : `${ingresosDiff >= 0 ? "+" : "−"}${fmtEur(Math.abs(ingresosDiff))}`;
  const ingresosTotal  = kpis?.ingresos?.neto ?? 0;
  const facturacionAnual = kpis?.ingresos?.bruto ?? 0;
  const abonosAnual      = kpis?.ingresos?.abonos ?? 0;
  const gastosTotal    = kpis?.gastos_anio ?? 0;
  const resultadoTotal = kpis?.resultado ?? 0;
  const pendienteTotal = kpis?.pendiente_cobro?.total ?? 0;
  const vencidasNum    = facturasPendientes?.vencidas?.num ?? 0;
  const porVencerNum   = facturasPendientes?.por_vencer?.num ?? 0;

  // Evolución → arrays para BarLineChart
  const meses = evolucion?.meses || [];
  const factCur = MONTHS.map((_, i) => meses[i]?.facturacion ?? 0);
  const factPrev = MONTHS.map((_, i) => meses[i]?.facturacion_ano_anterior ?? 0);
  const gastosArr = MONTHS.map((_, i) => meses[i]?.gasto ?? 0);
  // Proyección mensual (escenario base) para prolongar las barras de los meses restantes
  const margenPct = evolucion?.totales?.margen_porcentaje ?? null;
  const margenAbs = evolucion?.totales?.margen ?? null;
  const factTotal = evolucion?.totales?.facturacion ?? null;

  // Proyección de cierre de año (solo año en curso con meses cerrados)
  const proy = evolucion?.proyeccion || null;
  const proyMesLabel = proy?.mes_corte >= 1 ? MONTHS[proy.mes_corte - 1] : "";

  // Gastos por categoría → donut
  const catChartColors = [1,3,6,4,2,7,5,8,10,9].map(n => `var(--ce-chart-${n})`);
  const expCats = (gastos?.por_categoria || []).slice(0, 9).map((c, i) => ({
    label: c.nombre || "Sin nombre",
    cuenta: c.cuenta,
    value: c.total || 0,
    color: catChartColors[i % catChartColors.length],
  }));
  const totalGastosCuentas = gastos?.resumen?.num_cuentas ?? 0;

  // Saldos: aplanar { empresas: { 12: { cuentas: [...], total }, ... } }
  const bankRows = [];
  if (saldos?.empresas) {
    for (const [empId, info] of Object.entries(saldos.empresas)) {
      for (const c of (info.cuentas || [])) {
        bankRows.push({ ...c, empId });
      }
    }
  }
  const saldoTotal = saldos?.total_global ?? 0;

  // Facturas pendientes global: solo se usa para los badges vencidas/por vencer del
  // KPI anual "Pendiente de Cobro" (ver vencidasNum/porVencerNum más arriba).

  // Facturas sin cobrar del mes seleccionado (vista mensual)
  const invoicesMes = (facturasPendientesMes?.facturas || []).slice(0, 50);
  const totalPendienteMes = facturasPendientesMes?.total_pendiente ?? 0;
  const vencidasMesNum = facturasPendientesMes?.vencidas?.num ?? 0;
  const porVencerMesNum = facturasPendientesMes?.por_vencer?.num ?? 0;

  // Parciales (Datos parciales) — el shape es parcial.resumen.{ingresos,gastos,resultado,margen_pct}
  // donde cada uno es { actual, anterior, diferencia, variacion_pct }
  const pSum = parcial?.resumen || {};
  const pFac = pSum.facturacion || {};
  const pAbo = pSum.abonos      || {};
  const pIng = pSum.ingresos    || {};
  const pGas = pSum.gastos      || {};
  const pRes = pSum.resultado   || {};
  const pMar = pSum.margen_pct  || {};

  // Parciales — gastos por categoría (libro diario, mismo rango que las cards)
  const parcialCats = (parcial?.gastos?.por_categoria || []).slice(0, 10).map((c, i) => ({
    label: c.nombre || `Cuenta ${c.cuenta}`,
    value: c.total || 0,
    pct: `${(c.porcentaje ?? 0).toFixed(1).replace(".", ",")}%`,
    color: catChartColors[i % catChartColors.length],
  }));
  const parcialCatsCount = parcial?.gastos?.por_categoria?.length || 0;
  const parcialPeriodLabel = MESES[mes];

  // Top países anual (top 10) — datos del ERP, mismas fórmulas que ingresos.neto
  const paisesAnualRows = (paisesAnual?.paises || []).map((p, i) => ({
    label: p.pais || `País ${p.pais_id}`,
    value: p.total || 0,
    pct: `${(p.porcentaje ?? 0).toFixed(1).replace(".", ",")}%`,
    color: catChartColors[i % catChartColors.length],
  }));
  const paisesAnualTotal = paisesAnual?.total ?? 0;
  const paisesAnualCount = paisesAnual?.num_paises ?? 0;

  // Top países parcial (top 5) — mismo cálculo en el rango del periodo seleccionado
  const paisesParcialRows = (paisesParcial?.paises || []).map((p, i) => ({
    label: p.pais || `País ${p.pais_id}`,
    value: p.total || 0,
    pct: `${(p.porcentaje ?? 0).toFixed(1).replace(".", ",")}%`,
    color: catChartColors[i % catChartColors.length],
  }));
  const paisesParcialTotal = paisesParcial?.total ?? 0;
  const paisesParcialCount = paisesParcial?.num_paises ?? 0;

  // Top clientes anual (top 10) — datos del ERP
  const clientesAnualRows = (clientesAnual?.clientes || []).map((c, i) => ({
    label: c.cliente || `Cliente ${c.cliente_id}`,
    value: c.total || 0,
    pct: `${(c.porcentaje ?? 0).toFixed(1).replace(".", ",")}%`,
    color: catChartColors[i % catChartColors.length],
  }));
  const clientesAnualTotal = clientesAnual?.total ?? 0;
  const clientesAnualCount = clientesAnual?.num_clientes ?? 0;

  // Top clientes parcial (top 5)
  const clientesParcialRows = (clientesParcial?.clientes || []).map((c, i) => ({
    label: c.cliente || `Cliente ${c.cliente_id}`,
    value: c.total || 0,
    pct: `${(c.porcentaje ?? 0).toFixed(1).replace(".", ",")}%`,
    color: catChartColors[i % catChartColors.length],
  }));
  const clientesParcialTotal = clientesParcial?.total ?? 0;
  const clientesParcialCount = clientesParcial?.num_clientes ?? 0;

  // Aging de cobros — gradiente verde→rojo según antigüedad del impago
  const agingColors = {
    sin_fecha:  "var(--ce-fg-muted)",
    por_vencer: "var(--ce-success)",
    d1_30:      "#84cc16",
    d31_60:     "var(--ce-warning)",
    d61_90:     "#f59e0b",
    d90_plus:   "var(--ce-danger)",
  };
  const agingRows = (agingCobros?.buckets || []).map((b) => ({
    label: `${b.label} · ${b.num_facturas} ${b.num_facturas === 1 ? "factura" : "facturas"}`,
    value: b.total || 0,
    pct: `${(b.porcentaje ?? 0).toFixed(1).replace(".", ",")}%`,
    color: agingColors[b.id] || "var(--ce-primary)",
  }));
  const agingTotal = agingCobros?.total_pendiente ?? 0;
  const agingNumFact = agingCobros?.num_facturas ?? 0;
  const agingVencidoTotal = (agingCobros?.buckets || [])
    .filter((b) => b.id !== "por_vencer" && b.id !== "sin_fecha")
    .reduce((acc, b) => acc + (b.total || 0), 0);

  // DSO contable YTD
  const dsoDias = dso?.dso_dias;
  const dsoTone = dsoDias == null ? "primary" : dsoDias <= 30 ? "success" : dsoDias <= 60 ? "warning" : "danger";
  const dsoBenchmark = dsoDias == null
    ? "Sin ventas YTD todavía"
    : dsoDias <= 30 ? "≤30 d · cobro ágil"
    : dsoDias <= 60 ? "30–60 d · normal"
    : dsoDias <= 90 ? "60–90 d · ralentizado"
    : ">90 d · crítico";

  // Ticket medio anual: valor + variación + sparkline mensual (solo meses cerrados)
  const tmaActual = ticketMedioAnual?.actual;
  const tmaVar = ticketMedioAnual?.variacion;
  const tmaDelta = deltaPill(tmaVar?.variacion_pct);
  // Solo meses cerrados: el mes en curso y los futuros vienen con ticket = null.
  const tmaEvolMes = (ticketMedioAnual?.evolucion || [])
    .filter((m) => m.ticket != null)
    .map((m) => m.ticket);
  const tmaMesCorte = ticketMedioAnual?.mes_corte ?? 0;        // último mes cerrado (1-12)
  const tmaEsActual = ticketMedioAnual?.es_anio_actual;
  const tmaPeriodoLabel = tmaEsActual && tmaMesCorte >= 1
    ? `Ene–${MONTHS[tmaMesCorte - 1]}`
    : "Año completo";

  // Ticket medio parcial: pasamos por ParcialCard reusando el shape { actual, anterior, variacion_pct }
  const tmpObj = ticketMedioParcial
    ? {
        actual: ticketMedioParcial.actual?.ticket ?? 0,
        anterior: ticketMedioParcial.anterior?.ticket ?? 0,
        variacion_pct: ticketMedioParcial.variacion?.variacion_pct ?? null,
      }
    : {};

  // Eficiencia: ratio y runway
  const ratioActual = eficiencia?.ratio_pct?.actual;
  const ratioAnt = eficiencia?.ratio_pct?.anterior;
  const ratioDeltaPct = eficiencia?.ratio_pct?.variacion_pct;
  // Para ratio: bajar (más margen) es bueno; subir (menos margen) es malo.
  const ratioDeltaInv = ratioDeltaPct == null ? null : deltaPill(-ratioDeltaPct);
  const ratioTone = ratioActual == null ? "primary"
    : ratioActual <= 60 ? "success"
    : ratioActual <= 80 ? "warning"
    : ratioActual <= 100 ? "danger"
    : "danger";
  const ratioBenchmark = ratioActual == null
    ? "Sin datos"
    : ratioActual <= 60 ? "≤60% · margen sano"
    : ratioActual <= 80 ? "60–80% · normal"
    : ratioActual <= 100 ? "80–100% · ajustado"
    : ">100% · pierdes dinero";

  const runwayMeses = eficiencia?.runway_meses;
  const runwayTone = runwayMeses == null ? "primary"
    : runwayMeses >= 6 ? "success"
    : runwayMeses >= 3 ? "warning"
    : "danger";
  const runwayBenchmark = runwayMeses == null
    ? "Sin gastos registrados"
    : runwayMeses >= 12 ? "≥12 m · holgado"
    : runwayMeses >= 6 ? "6–12 m · cómodo"
    : runwayMeses >= 3 ? "3–6 m · vigilar"
    : "<3 m · crítico";

  return (
    <>
      <PageHead
        icon={I.ChartLine}
        title="Dashboard Financiero"
        sub={vista === "anual"
          ? `Datos anuales · empresa ${empresaLabel(empresaId)} · año ${year}`
          : `Datos mensuales · empresa ${empresaLabel(empresaId)} · ${MESES[mes]}`}
        screenId="finanzas"
      />

      {vista === "anual" && (
      <>
      {/* KPI hero row */}
      <div className="grid gc-4">
        <KPI
          tier="hero" tone="primary" icon={I.Wallet}
          label="Ingresos del Año"
          value={loading?.kpis ? "—" : fmtEur(ingresosTotal)}
          delta={ingresosDelta?.text} deltaTone={ingresosDelta?.tone}
          deltaRef={ingresosDiffTxt ? `${ingresosDiffTxt} vs año anterior` : "vs año anterior"}
          extras={!loading?.kpis ? (
            <span className="kpi-ref">
              Facturación {fmtEur(facturacionAnual)} · Abonos −{fmtEur(abonosAnual)}
            </span>
          ) : null}
          info={{
            descripcion: "Ingreso neto del año = facturación (facturas emitidas) menos abonos. Debajo se desglosa la facturación bruta y el importe de abonos descontado.",
            origen: "BD erpvisados_2023 — facturasproyectolineas, facturas, proyectos, abonolineas, abonos.",
            formula: "Facturación = Σ ((actividad_precio + importe) × cantidad_facturada × (1 − descuento/100)). Ingreso neto = Facturación − Σ abonos del año.",
          }}
        />
        <KPI
          tier="primary" tone="danger" icon={I.Cart}
          label="Gastos del Año"
          value={loading?.kpis ? "—" : fmtEur(gastosTotal)}
          info={{
            descripcion: "Gastos totales contabilizados en Holded a lo largo del año seleccionado.",
            origen: "API Holded — /invoicing/v1/documents/purchase, una llamada por empresa (Visados, Permiso, CVS).",
            formula: "Σ subtotal de las compras con date dentro del año.",
          }}
        />
        <KPI
          tier="primary" tone="success" icon={I.TrendUp}
          label="Resultado"
          value={loading?.kpis ? "—" : fmtEur(resultadoTotal)}
          info={{
            descripcion: "Diferencia entre los ingresos netos del año y los gastos contabilizados en Holded.",
            origen: "Calculado en cliente a partir de ingresos (BD ERP) y gastos (Holded).",
            formula: "Ingresos netos − Gastos del año.",
          }}
        />
        <KPI
          tier="primary" tone="warning" icon={I.Clock}
          label="Pendiente de Cobro"
          value={loading?.kpis ? "—" : fmtEur(pendienteTotal)}
          info={{
            descripcion: "Total y número de facturas con Cobrada = 0 (no cobradas todavía), de cualquier año.",
            origen: "BD erpvisados_2023 — facturasproyectolineas + facturas (Cobrada IS NULL OR 0).",
            formula: "Σ importe de líneas de facturas no cobradas (sin filtro de año), excluidas eliminadas y propuestas de valor.",
          }}
          extras={
            <>
              {vencidasNum > 0 && <Badge tone="neg">{vencidasNum} vencidas</Badge>}
              {porVencerNum > 0 && <Badge tone="warn">{porVencerNum} por vencer</Badge>}
            </>
          }
        />
      </div>

      {/* Proyección de cierre de año */}
      {proy && (
      <div style={{ marginTop: 20 }}>
        <ChartCard
          icon={I.TrendUp} title="Proyección de Cierre de Año"
          info={{
            titulo: "Proyección de Cierre de Año",
            descripcion: "Estimación de cómo cerrará el año en ingresos, gastos y resultado, a partir de lo ya facturado/gastado en los meses cerrados. Dos escenarios: el BASE (estacional) proyecta los meses que faltan con la forma del año anterior escalada por tu crecimiento real; el CONSERVADOR (lineal) simplemente extrapola la media mensual × 12. El mes en curso no se incluye en lo real para no contar datos incompletos.",
            origen: "Ingresos = facturación neta del ERP (erpvisados_2023). Gastos = grupo 6 PGC del libro diario de Holded. Mismos importes que el KPI Resultado. Forma estacional tomada del mismo año anterior.",
            formula: "Estacional = YTD_real + Σ(mes_año_anterior × factor), factor = YTD_real / YTD_mismos_meses_año_anterior. Lineal = YTD_real / nº_meses_cerrados × 12. Resultado = Ingresos − Gastos.",
          }}
          meta={<span>{proy.meses_cerrados}/12 meses cerrados · {fmtPct(proy.avance_pct)} del año</span>}
          foot={
            <>
              <span>Real a cierre de <strong style={{color:"var(--ce-fg-2)"}}>{proyMesLabel}</strong></span>
              {proy.ingresos.factor_crecimiento != null && (
                <span>Ritmo ingresos vs {year - 1}: <span className={`pos`}>{fmtPct((proy.ingresos.factor_crecimiento - 1) * 100)}</span></span>
              )}
            </>
          }
        >
          <div className="grid gc-5-7" style={{ alignItems: "center", gap: 20 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <span style={{ fontSize: 12, color: "var(--ce-fg-muted)" }}>Resultado proyectado a cierre (lineal)</span>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 44, fontWeight: 700, lineHeight: 1, color: "var(--ce-fg)", fontVariantNumeric: "tabular-nums" }}>
                  {fmtEur(proy.resultado.lineal)}
                </span>
              </div>
              <div style={{ display: "flex", gap: 18, marginTop: 6, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, color: "var(--ce-fg-muted)" }}>
                  Ingresos <strong style={{ color: "var(--ce-green-ink)" }}>{fmtEur(proy.ingresos.lineal)}</strong>
                </span>
                <span style={{ fontSize: 13, color: "var(--ce-fg-muted)" }}>
                  Gastos <strong style={{ color: "var(--ce-amber-ink)" }}>{fmtEur(proy.gastos.lineal)}</strong>
                </span>
              </div>
            </div>
            <table className="tbl" style={{ tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th>Escenario</th>
                  <th className="right">Ingresos</th>
                  <th className="right">Gastos</th>
                  <th className="right">Resultado</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ color: "var(--ce-fg-muted)" }}>YTD real · Ene–{proyMesLabel}</td>
                  <td className="right amount">{fmtEur(proy.ingresos.ytd)}</td>
                  <td className="right amount">{fmtEur(proy.gastos.ytd)}</td>
                  <td className="right amount">{fmtEur(proy.resultado.ytd)}</td>
                </tr>
                <tr>
                  <td><strong style={{ color: "var(--ce-fg-2)" }}>Proyección lineal</strong></td>
                  <td className="right amount"><strong>{fmtEur(proy.ingresos.lineal)}</strong></td>
                  <td className="right amount"><strong>{fmtEur(proy.gastos.lineal)}</strong></td>
                  <td className="right amount"><strong>{fmtEur(proy.resultado.lineal)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </ChartCard>
      </div>
      )}

      {/* Ticket medio anual */}
      <div style={{ marginTop: 20 }}>
        <ChartCard
          icon={I.Coin} title="Ticket Medio por Factura"
          info={{
            titulo: "Ticket Medio por Factura",
            descripcion: "Importe neto medio de cada factura emitida. Sube cuando vendemos más caro por operación (mejor mix de servicios o clientes), baja cuando crecemos a base de ticket pequeño. Solo cuenta los meses cerrados: el mes en curso se excluye porque aún no tiene todos los abonos cargados y distorsionaría la media. La comparativa interanual usa el mismo rango de meses del año anterior (like-for-like).",
            origen: "BD erpvisados_2023 — facturasproyectolineas + facturas + proyectos + abonos. Misma fórmula que el KPI Ingresos del Año.",
            formula: "Ticket = (Σ LINEA_IMPORTE − Σ ABONO_IMPORTE) / COUNT(DISTINCT facturas.id), acumulado de los meses cerrados (Ene–último mes cerrado).",
          }}
          meta={<span>Año {year} · {tmaPeriodoLabel}</span>}
          foot={
            tmaActual ? (
              <>
                <span>{fmtN(tmaActual.num_facturas)} facturas · <strong style={{color:"var(--ce-fg-2)"}}>{fmtEur(tmaActual.neto)}</strong> neto · meses cerrados</span>
                {ticketMedioAnual?.anterior?.num_facturas ? (
                  <span>{year - 1} ({tmaPeriodoLabel}): {fmtEur(ticketMedioAnual.anterior.ticket)} ({fmtN(ticketMedioAnual.anterior.num_facturas)} facturas)</span>
                ) : null}
              </>
            ) : null
          }
        >
          {tmaActual ? (
            <div className="grid gc-7-5" style={{ alignItems: "center", gap: 20 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 48, fontWeight: 700, lineHeight: 1, color: "var(--ce-fg)", fontVariantNumeric: "tabular-nums" }}>
                  {fmtEur(tmaActual.ticket)}
                </span>
                <span style={{ fontSize: 14, color: "var(--ce-fg-muted)" }}>por factura</span>
                {tmaDelta ? (
                  <span className={`bdg ${tmaDelta.tone}`}>
                    <span className="dot" />{tmaDelta.text} vs {year - 1}
                  </span>
                ) : null}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ fontSize: 12, color: "var(--ce-fg-muted)" }}>Evolución mensual (meses cerrados)</span>
                <Sparkline values={tmaEvolMes} color="var(--ce-primary)" w={240} h={60} responsive />
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--ce-fg-faint)" }}>
                  <span>{MONTHS[0]}</span>
                  <span>{tmaMesCorte >= 1 ? MONTHS[tmaMesCorte - 1] : MONTHS[11]}</span>
                </div>
              </div>
            </div>
          ) : (
            <Empty icon={I.Coin} title="Sin facturas este año" />
          )}
        </ChartCard>
      </div>

      {/* Charts row */}
      <div className="grid gc-8-4" style={{ marginTop: 20 }}>
        <ChartCard
          icon={I.BarChart3} title="Facturación vs Gastos"
          info={{
            titulo: "Facturación vs Gastos",
            descripcion: "Comparativa mensual de facturación neta del ERP frente a gastos contabilizados en Holded, con la línea de facturación del año anterior como referencia.",
            origen: "BD erpvisados_2023 (facturas, facturasproyectolineas, proyectolineas, abonos, abonolineas, proyectos) + Holded API (purchases).",
            formula: "Facturación neta = Σ (precio_línea + importe) × cantidad × (1 − descuento/100) − abonos. Gastos = Σ subtotal de purchases de Holded por mes.",
          }}
          meta={
            margenAbs != null ? (
              <>
                <span>Margen {fmtEur(margenAbs)}</span>
                {margenPct != null && (
                  <span className={`bdg ${margenPct >= 0 ? "pos" : "neg"}`}>
                    <span className="dot"/>{margenPct >= 0 ? "↑" : "↓"} {Math.abs(margenPct).toFixed(1).replace(".", ",")}%
                  </span>
                )}
              </>
            ) : null
          }
          legend={<Legend items={[
            { color: "var(--ce-chart-1)", label: `Facturación ${year}` },
            { color: "var(--ce-chart-6)", label: "Gastos" },
            { color: "var(--ce-chart-previous)", label: `Facturación ${year - 1}`, dashed: true },
          ]}/>}
          foot={
            factTotal != null ? (
              <>
                <span>Total facturado · <strong style={{color:"var(--ce-fg-2)"}}>{fmtEur(factTotal)}</strong></span>
                {margenAbs != null && margenPct != null && (
                  <span><strong style={{color:"var(--ce-fg-2)"}}>Margen</strong> {fmtEur(margenAbs)} · <span className="pos">{fmtPct(margenPct)}</span></span>
                )}
              </>
            ) : null
          }
        >
          {meses.length ? (
            <BarLineChart months={MONTHS} cur={factCur} prev={factPrev} expenses={gastosArr} />
          ) : (
            <Empty icon={I.BarChart3} title="Sin datos de evolución" sub="Espera unos segundos o pulsa Actualizar." />
          )}
        </ChartCard>

        <ChartCard
          icon={I.PieChart} title="Gastos por Categoría"
          info={{
            titulo: "Gastos por Categoría",
            descripcion: "Top 10 proveedores/contactos por importe, con totales pagado vs pendiente de pago según Holded.",
            origen: "API Holded — /invoicing/v1/documents/purchase, agrupado por contactName.",
            formula: "Σ subtotal de compras del año por proveedor; pagado = Σ paymentsTotal, pendiente = Σ paymentsPending.",
          }}
          meta={<span>Cuentas 6XX · PGC</span>}
          foot={
            gastos ? (
              <>
                <span>Total · <strong style={{color:"var(--ce-fg-2)"}}>{fmtEur(gastosTotal)}</strong></span>
                <span>{totalGastosCuentas} cuentas contables</span>
              </>
            ) : null
          }
        >
          {expCats.length ? (
            <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
              <DonutChart data={expCats} totalLabel="Total" totalValueFmt={fmtEur} size={180} />
              <DonutLegend data={expCats.slice(0, 7)} totalForPct={expCats.reduce((a,b)=>a+b.value,0)} />
            </div>
          ) : (
            <Empty icon={I.PieChart} title="Sin gastos contabilizados" />
          )}
        </ChartCard>
      </div>

      {/* Tables row — Saldos a ancho completo (Facturas Pendientes se movió a la vista mensual) */}
      <div style={{ marginTop: 20 }}>
        <ChartCard
          icon={I.Bank} title="Saldos Bancarios"
          info={{
            titulo: "Saldos Bancarios",
            descripcion: "Saldo en tiempo real de cada cuenta bancaria/tarjeta de las tres empresas, con el banco inferido del nombre.",
            origen: "API Holded — /invoicing/v1/treasury, una llamada por empresa.",
            formula: "Para cada cuenta con balance ≠ 0: saldo = balance. Total = Σ balance de todas las cuentas.",
          }}
          meta={
            <>
              <span>Total consolidado</span>
              <strong style={{ color: "var(--ce-fg)", fontVariantNumeric: "tabular-nums" }}>{fmtEur(saldoTotal)}</strong>
            </>
          }
        >
          {bankRows.length ? (
            <table className="tbl">
              <thead>
                <tr><th>Cuenta</th><th>Banco</th><th className="right">Saldo</th></tr>
              </thead>
              <tbody>
                {bankRows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.nombre}</td>
                    <td>{r.banco ? <BankChip bank={r.banco}/> : <span style={{color: "var(--ce-fg-faint)"}}>—</span>}</td>
                    <td className="right amount">{fmtEur(r.saldo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty icon={I.Bank} title="Sin saldos bancarios" sub="Holded no devolvió cuentas para esta empresa." />
          )}
        </ChartCard>
      </div>

      {/* Aging de cobros + DSO */}
      <div className="grid gc-7-5" style={{ marginTop: 20 }}>
        <ChartCard
          icon={I.Clock} title="Aging de Cobros"
          info={{
            titulo: "Aging de Cobros",
            descripcion: "Antigüedad del importe pendiente de cobro: las facturas no cobradas se reparten en buckets según los días que llevan vencidas (DATEDIFF entre hoy y FechaPrevistaDePago). Mismo universo que el KPI 'Pendiente de Cobro'.",
            origen: "BD erpvisados_2023 — facturasproyectolineas + facturas + proyectos (Cobrada IS NULL OR 0, eliminada = 0).",
            formula: "Σ (actividad_precio + importe) × cantidad_facturada × (1 − descuento/100) por factura, clasificado en por_vencer / 1–30 / 31–60 / 61–90 / >90 días.",
          }}
          meta={
            agingCobros ? (
              <>
                <strong style={{ color: "var(--ce-amber-ink)" }}>{fmtEur(agingTotal)}</strong>
                <span>{agingNumFact} facturas</span>
                {agingVencidoTotal > 0 && (
                  <span className="bdg neg"><span className="dot" />Vencido {fmtEur(agingVencidoTotal)}</span>
                )}
              </>
            ) : null
          }
        >
          {agingRows.length ? (
            <HorizontalBarList data={agingRows} fmt={fmtEur} />
          ) : (
            <Empty ok icon={I.Check} title="Sin facturas pendientes" sub="Todas las facturas están cobradas." />
          )}
        </ChartCard>

        <ChartCard
          icon={I.Calendar} title="DSO Contable"
          info={{
            titulo: "DSO Contable (Days Sales Outstanding)",
            descripcion: "Días promedio que tardan en convertirse las ventas en cobros. Se interpreta como 'cuántos días de venta YTD tengo aún sin cobrar'. Cuanto más bajo, mejor. Es independiente del filtro de año del dashboard: siempre se calcula contra el año en curso.",
            origen: "BD erpvisados_2023 — facturasproyectolineas + facturas + proyectos + abonos. Mismo universo que el KPI Pendiente y el KPI Ingresos del Año.",
            formula: "DSO = (Pendiente de Cobro / Ingresos netos YTD) × días transcurridos del año.",
          }}
          meta={
            dso ? <span>{dso.fecha_corte} · YTD {dso.dias_transcurridos} d</span> : null
          }
        >
          {dso ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 56, fontWeight: 700, lineHeight: 1, color: "var(--ce-fg)", fontVariantNumeric: "tabular-nums" }}>
                  {dsoDias == null ? "—" : Math.round(dsoDias)}
                </span>
                <span style={{ fontSize: 16, color: "var(--ce-fg-2)" }}>días</span>
                <span className={`bdg ${dsoTone === "success" ? "pos" : dsoTone === "warning" ? "warn" : dsoTone === "danger" ? "neg" : "neu"}`}>
                  <span className="dot" />{dsoBenchmark}
                </span>
              </div>
              <table className="tbl" style={{ tableLayout: "fixed" }}>
                <tbody>
                  <tr>
                    <td style={{ color: "var(--ce-fg-muted)" }}>Pendiente de cobro</td>
                    <td className="right amount">{fmtEur(dso.pendiente)}</td>
                  </tr>
                  <tr>
                    <td style={{ color: "var(--ce-fg-muted)" }}>÷ Ingresos netos YTD</td>
                    <td className="right amount">{fmtEur(dso.ingresos_ytd)}</td>
                  </tr>
                  <tr>
                    <td style={{ color: "var(--ce-fg-muted)" }}>× Días transcurridos</td>
                    <td className="right amount">{fmtN(dso.dias_transcurridos)} d</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <Empty icon={I.Calendar} title="Calculando DSO…" />
          )}
        </ChartCard>
      </div>

      {/* Eficiencia: ratio gastos/ingresos + runway */}
      <div className="grid gc-2" style={{ marginTop: 20 }}>
        <ChartCard
          icon={I.Percent} title="Ratio Gastos / Ingresos"
          info={{
            titulo: "Ratio Gastos / Ingresos (eficiencia operativa)",
            descripcion: "Porcentaje de cada euro facturado que se va en gastos. Complementario al margen: si ratio = 70%, margen = 30%. Es termómetro de eficiencia: cuando sube sin que crezca la facturación, hay descontrol de costes.",
            origen: "Ingresos del ERP (LINEA − ABONO) ÷ Gastos del libro diario de Holded (grupo 6 PGC).",
            formula: "Ratio = (Gastos / Ingresos netos) × 100. El rango se ajusta YTD si el año está en curso, completo si está cerrado.",
          }}
          meta={
            eficiencia ? (
              <span>{eficiencia.es_anio_actual ? `YTD ${eficiencia.dias_periodo} d` : `Año completo`}</span>
            ) : null
          }
        >
          {eficiencia ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 56, fontWeight: 700, lineHeight: 1, color: "var(--ce-fg)", fontVariantNumeric: "tabular-nums" }}>
                  {ratioActual == null ? "—" : ratioActual.toFixed(1).replace(".", ",")}
                </span>
                <span style={{ fontSize: 16, color: "var(--ce-fg-2)" }}>%</span>
                <span className={`bdg ${ratioTone === "success" ? "pos" : ratioTone === "warning" ? "warn" : ratioTone === "danger" ? "neg" : "neu"}`}>
                  <span className="dot" />{ratioBenchmark}
                </span>
                {ratioDeltaInv ? (
                  <span className={`bdg ${ratioDeltaInv.tone}`}>
                    <span className="dot" />{ratioDeltaInv.text} vs año anterior
                  </span>
                ) : null}
              </div>
              <table className="tbl" style={{ tableLayout: "fixed" }}>
                <tbody>
                  <tr>
                    <td style={{ color: "var(--ce-fg-muted)" }}>Ingresos netos</td>
                    <td className="right amount">{fmtEur(eficiencia.ingresos.actual)}</td>
                  </tr>
                  <tr>
                    <td style={{ color: "var(--ce-fg-muted)" }}>Gastos del periodo</td>
                    <td className="right amount">{fmtEur(eficiencia.gastos.actual)}</td>
                  </tr>
                  <tr>
                    <td style={{ color: "var(--ce-fg-muted)" }}>Margen resultante</td>
                    <td className="right amount" style={{ color: "var(--ce-green-ink)" }}>
                      {eficiencia.margen_pct?.actual == null ? "—" : `${eficiencia.margen_pct.actual.toFixed(1).replace(".", ",")}%`}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <Empty icon={I.Percent} title="Calculando ratio…" />
          )}
        </ChartCard>

        <ChartCard
          icon={I.Wallet} title="Runway"
          info={{
            titulo: "Runway (meses de colchón)",
            descripcion: "Si dejara de facturar mañana, cuántos meses duraría la caja al ritmo de gasto actual. Es el colchón frente a un mal trimestre o caída de ventas. No es métrica de supervivencia para una empresa rentable, pero sí indica margen de maniobra.",
            origen: "Caja: Holded /invoicing/v1/treasury (suma de cuentas con balance ≠ 0). Burn rate: gasto del periodo / días × 30,44.",
            formula: "Runway = Caja actual / (Gastos del periodo / días del periodo × 30,44).",
          }}
          meta={
            eficiencia ? <span>{eficiencia.fecha_corte}</span> : null
          }
        >
          {eficiencia ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 56, fontWeight: 700, lineHeight: 1, color: "var(--ce-fg)", fontVariantNumeric: "tabular-nums" }}>
                  {runwayMeses == null ? "—" : runwayMeses.toFixed(1).replace(".", ",")}
                </span>
                <span style={{ fontSize: 16, color: "var(--ce-fg-2)" }}>meses</span>
                <span className={`bdg ${runwayTone === "success" ? "pos" : runwayTone === "warning" ? "warn" : runwayTone === "danger" ? "neg" : "neu"}`}>
                  <span className="dot" />{runwayBenchmark}
                </span>
              </div>
              <table className="tbl" style={{ tableLayout: "fixed" }}>
                <tbody>
                  <tr>
                    <td style={{ color: "var(--ce-fg-muted)" }}>Caja actual</td>
                    <td className="right amount">{fmtEur(eficiencia.caja_actual)}</td>
                  </tr>
                  <tr>
                    <td style={{ color: "var(--ce-fg-muted)" }}>÷ Burn rate mensual</td>
                    <td className="right amount">{fmtEur(eficiencia.burn_rate_mensual)}</td>
                  </tr>
                  <tr>
                    <td style={{ color: "var(--ce-fg-muted)" }}>Gastos en {eficiencia.dias_periodo} d</td>
                    <td className="right amount">{fmtEur(eficiencia.gastos.actual)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <Empty icon={I.Wallet} title="Calculando runway…" />
          )}
        </ChartCard>
      </div>

      {/* Top clientes + países anual */}
      <div className="grid gc-2" style={{ marginTop: 20 }}>
        <ChartCard
          icon={I.Users} title="Top 10 Clientes por Facturación"
          info={{
            titulo: "Top 10 Clientes por Facturación",
            descripcion: "Ranking de clientes que más facturan en el año. La cifra es la facturación neta (líneas de factura menos abonos), igual que el KPI de Ingresos del Año.",
            origen: "BD erpvisados_2023 — facturasproyectolineas + facturas + proyectos + abonos + clientes (JOIN por facturas.cliente_id).",
            formula: "Σ ((actividad_precio + importe) × cantidad_facturada × (1 − descuento/100)) − Σ abonos del año, agrupado por cliente y ordenado descendente.",
          }}
          meta={<span>Año {year}</span>}
          foot={
            clientesAnual ? (
              <>
                <span>Total · <strong style={{color:"var(--ce-fg-2)"}}>{fmtEur(clientesAnualTotal)}</strong></span>
                <span>{clientesAnualCount} clientes facturados</span>
              </>
            ) : null
          }
        >
          {clientesAnualRows.length ? (
            <HorizontalBarList data={clientesAnualRows} fmt={fmtEur} />
          ) : (
            <Empty icon={I.Users} title="Sin facturación por cliente este año" />
          )}
        </ChartCard>

        <ChartCard
          icon={I.Globe} title="Top 10 Países por Facturación"
          info={{
            titulo: "Top 10 Países por Facturación",
            descripcion: "Ranking de países que más facturan en el año, calculado a partir de los proyectos asociados a cada país. La cifra es la facturación neta (líneas de factura menos abonos), igual que el KPI de Ingresos del Año.",
            origen: "BD erpvisados_2023 — facturasproyectolineas + facturas + proyectos + abonos + paises (JOIN por proyectos.pais_id).",
            formula: "Σ ((actividad_precio + importe) × cantidad_facturada × (1 − descuento/100)) − Σ abonos del año, agrupado por país y ordenado descendente.",
          }}
          meta={<span>Año {year}</span>}
          foot={
            paisesAnual ? (
              <>
                <span>Total · <strong style={{color:"var(--ce-fg-2)"}}>{fmtEur(paisesAnualTotal)}</strong></span>
                <span>{paisesAnualCount} países con facturación</span>
              </>
            ) : null
          }
        >
          {paisesAnualRows.length ? (
            <HorizontalBarList data={paisesAnualRows} fmt={fmtEur} />
          ) : (
            <Empty icon={I.Globe} title="Sin facturación por país este año" />
          )}
        </ChartCard>
      </div>

      </>
      )}

      {vista === "mensual" && (
      <>
      {/* Datos parciales */}
      <SectionHead
        icon={I.Calendar} title="Datos parciales"
        sub="Comparativa contra el mes anterior"
        right={
          <select className="sel" value={mes} onChange={(e) => setMes(Number(e.target.value))} aria-label="Mes">
            {MESES_OPCIONES.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        }
      />
      <div className="grid" style={{ gridTemplateColumns: "repeat(5, 1fr)" }}>
        <ParcialCard tone="success" icon={I.FileText} label="Facturación" obj={pFac} fmt={fmtEur}
          info={{
            descripcion: "Facturación bruta del ERP (líneas de factura emitidas, sin restar abonos) en el mes seleccionado, comparada con el mes anterior.",
            origen: "BD erpvisados_2023 — facturasproyectolineas + facturas + proyectos.",
            formula: "Σ ((actividad_precio + importe) × cantidad_facturada × (1 − descuento/100)) del rango.",
          }}
        />
        <ParcialCard tone="warning" icon={I.TrendDown} label="Abonos" obj={pAbo} fmt={fmtEur} flipDelta
          info={{
            descripcion: "Importe de abonos (rectificaciones / devoluciones) emitidos en el mes seleccionado, comparado con el mes anterior. Se descuenta de la facturación para obtener el ingreso neto.",
            origen: "BD erpvisados_2023 — facturasproyectolineas + abonolineas + abonos.",
            formula: "Σ abonos (importe + actividad_precio) × porcentaje_abonado/100 × cantidad × (1 − descuento/100), por fecha de abono.",
          }}
        />
        <ParcialCard tone="primary" icon={I.Coin}     label="Ingreso neto" obj={pIng} fmt={fmtEur}
          info={{
            descripcion: "Ingreso neto del ERP (facturación menos abonos) en el mes seleccionado, comparado con el mes anterior. Misma fórmula que el KPI anual de Ingresos.",
            origen: "BD erpvisados_2023 — facturasproyectolineas + facturas + proyectos + abonolineas + abonos.",
            formula: "Facturación − Abonos del rango.",
          }}
        />
        <ParcialCard tone="danger"  icon={I.Cart}     label="Gastos"    obj={pGas} fmt={fmtEur}
          info={{
            descripcion: "Compras del rango seleccionado contra el periodo anterior equivalente.",
            origen: "API Holded — /invoicing/v1/documents/purchase (todas las empresas).",
            formula: "Σ subtotal de compras con date dentro del rango.",
          }}
        />
        <ParcialCard tone="primary" icon={I.TrendUp}  label="Resultado" obj={pRes} fmt={fmtEur}
          info={{
            descripcion: "Diferencia entre ingresos y gastos del rango, con la variación frente al periodo anterior.",
            origen: "Calculado en API a partir de facturas y compras de Holded.",
            formula: "Ingresos − Gastos.",
          }}
        />
        <ParcialCard tone="cyan"    icon={I.Percent}  label="Margen"    obj={pMar} fmt={(v) => fmtPct(v)} flipDelta
          info={{
            descripcion: "Porcentaje del resultado sobre los ingresos del rango.",
            origen: "Calculado en API a partir de Holded.",
            formula: "(Ingresos − Gastos) / Ingresos × 100.",
          }}
        />
        <ParcialCard tone="primary" icon={I.Coin}     label="Ticket Medio" obj={tmpObj} fmt={fmtEur}
          info={{
            descripcion: "Importe neto medio por factura emitida en el rango seleccionado, comparado con el periodo anterior equivalente.",
            origen: "BD erpvisados_2023 — facturasproyectolineas + facturas + proyectos + abonos.",
            formula: "Ticket = (Σ LINEA_IMPORTE − Σ ABONO_IMPORTE) / COUNT(DISTINCT facturas.id) del rango.",
          }}
        />
      </div>

      <div className="grid gc-3" style={{ marginTop: 20 }}>
        <ChartCard
          icon={I.Users} title="Top 5 Clientes por Facturación · periodo"
          info={{
            titulo: "Top 5 Clientes por Facturación (periodo parcial)",
            descripcion: "Ranking de los 5 clientes que más facturan en el rango seleccionado.",
            origen: "BD erpvisados_2023 — facturasproyectolineas + facturas + proyectos + abonos + clientes (JOIN por facturas.cliente_id).",
            formula: "Σ ((actividad_precio + importe) × cantidad_facturada × (1 − descuento/100)) − Σ abonos del rango, agrupado por cliente.",
          }}
          meta={<span>{parcialPeriodLabel}</span>}
          foot={
            clientesParcial ? (
              <>
                <span>Total · <strong style={{color:"var(--ce-fg-2)"}}>{fmtEur(clientesParcialTotal)}</strong></span>
                <span>{clientesParcialCount} clientes facturados</span>
              </>
            ) : null
          }
        >
          {clientesParcialRows.length ? (
            <HorizontalBarList data={clientesParcialRows} fmt={fmtEur} />
          ) : (
            <Empty icon={I.Users} title="Sin facturación por cliente en este periodo" />
          )}
        </ChartCard>

        <ChartCard
          icon={I.BarChart3} title="Gastos por Categoría · periodo"
          info={{
            titulo: "Gastos por Categoría (periodo parcial)",
            descripcion: "Desglose de gastos del rango seleccionado, agrupados por cuenta contable del PGC.",
            origen: "API Holded — /accounting/v1/dailyledger, agregado por nº de cuenta (grupo 6).",
            formula: "Σ (débito − crédito) por cuenta dentro del rango actual. Porcentaje sobre el total de gastos del periodo.",
          }}
          meta={<span>{parcialPeriodLabel}</span>}
          foot={
            parcial ? (
              <>
                <span>Total · <strong style={{color:"var(--ce-fg-2)"}}>{fmtEur(pGas.actual ?? 0)}</strong></span>
                <span>{parcialCatsCount} cuentas contables</span>
              </>
            ) : null
          }
        >
          {parcialCats.length ? (
            <HorizontalBarList data={parcialCats} fmt={fmtEur} />
          ) : (
            <Empty icon={I.BarChart3} title="Sin gastos en este periodo" />
          )}
        </ChartCard>

        <ChartCard
          icon={I.Globe} title="Top 5 Países por Facturación · periodo"
          info={{
            titulo: "Top 5 Países por Facturación (periodo parcial)",
            descripcion: "Ranking de los 5 países que más facturan en el rango seleccionado, calculado a partir de los proyectos asociados a cada país.",
            origen: "BD erpvisados_2023 — facturasproyectolineas + facturas + proyectos + abonos + paises (JOIN por proyectos.pais_id).",
            formula: "Σ ((actividad_precio + importe) × cantidad_facturada × (1 − descuento/100)) − Σ abonos del rango, agrupado por país.",
          }}
          meta={<span>{parcialPeriodLabel}</span>}
          foot={
            paisesParcial ? (
              <>
                <span>Total · <strong style={{color:"var(--ce-fg-2)"}}>{fmtEur(paisesParcialTotal)}</strong></span>
                <span>{paisesParcialCount} países con facturación</span>
              </>
            ) : null
          }
        >
          {paisesParcialRows.length ? (
            <HorizontalBarList data={paisesParcialRows} fmt={fmtEur} />
          ) : (
            <Empty icon={I.Globe} title="Sin facturación por país en este periodo" />
          )}
        </ChartCard>
      </div>

      {/* Facturas sin cobrar del mes seleccionado */}
      <div style={{ marginTop: 20 }}>
        <ChartCard
          icon={I.FileText} title="Facturas Pendientes de Cobro · mes"
          info={{
            titulo: "Facturas Pendientes de Cobro (mes seleccionado)",
            descripcion: "Facturas no cobradas cuya fecha de factura cae dentro del mes seleccionado, ordenadas por fecha prevista de pago.",
            origen: "BD erpvisados_2023 — facturas LEFT JOIN clientes (Cobrada IS NULL OR 0, eliminada = 0), filtradas por FechaDeFactura del mes.",
            formula: "Vencida = DATEDIFF(NOW, FechaPrevistaDePago) > 0. Total pendiente = Σ importe de líneas.",
          }}
          meta={
            <>
              <strong style={{ color: "var(--ce-amber-ink)" }}>{fmtEur(totalPendienteMes)}</strong>
              <span>{vencidasMesNum} vencidas · {porVencerMesNum} por vencer · {parcialPeriodLabel}</span>
            </>
          }
        >
          {invoicesMes.length ? (
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{width:60}}>#</th>
                  <th>Cliente</th>
                  <th className="right">Importe</th>
                  <th className="right" style={{width:130}}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {invoicesMes.map((r, i) => (
                  <tr key={i}>
                    <td className="num">{r.numero || r.id}</td>
                    <td>{r.cliente || "—"}</td>
                    <td className="right amount">{fmtEur(r.total || 0)}</td>
                    <td className="right">
                      {r.vencida
                        ? <Badge tone="neg">Vencida {r.dias_vencida}d</Badge>
                        : <Badge tone="warn">Por vencer</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty ok icon={I.Check} title="Sin facturas pendientes en este mes" sub="Todas las facturas del mes están cobradas." />
          )}
        </ChartCard>
      </div>
      </>
      )}

      <Footer />
    </>
  );
};

const ParcialCard = ({ tone, icon, label, obj, fmt = fmtEur, flipDelta = false, info }) => {
  const actual = obj?.actual ?? 0;
  const anterior = obj?.anterior ?? 0;
  const pct = obj?.variacion_pct;
  const pill = deltaPill(pct);
  return (
    <CompactKPI
      tone={tone} icon={icon} label={label}
      value={fmt(actual)}
      prev={anterior != null ? fmt(anterior) : null}
      delta={pill?.text}
      deltaTone={pill?.tone}
      info={info}
    />
  );
};

const empresaLabel = (id) => {
  if (id === "all") return "Todas";
  if (id === 13) return "Visados";
  if (id === 12) return "Permiso";
  if (id === 14) return "CVS";
  if (id === "visados_cvs") return "Visados+CVS";
  return String(id);
};

export default DashboardFinanzas;
